import { selectSpeechLang } from '../selectors/legacy-conversation';
import { selectCurrentSelectedVoice } from '../selectors/textToSpeech';
import { muteMicForTts, unmuteMicForTts } from '../store/slices/audioV2';
import type { TtsErrors, TtsGender, TtsVoice, TtSVoiceEntry } from '../store/slices/textToSpeech';
import { setTtsError, setUserTyping } from '../store/slices/textToSpeech';
import type { AppDispatch, RootState } from '../store/store';
import {
  checkEndsWithPunctuation,
  KARAOKE_DELAY_MAPPING,
  KARAOKE_PRE_DELAY_MAPPING,
  PUNCTUATION_DELAYS,
} from './karaoke';
import { setTtsPlaybackActive } from './webrtc';
import { sendWsMessage } from './ws-v1';

export interface SignalIAmTyping {
  type: 'signal-i-am-typing';
  fromUser: UserTyping;
}

export interface ResponseTextToSpeech {
  type: 'response-text-to-speech';
  isDone: boolean;
  error: string;
}

export interface RoomStatusUpdate {
  type: 'room-status-update';
  reason: string;
  participants: Participant[];
}

// this isn't everything in participant, but all we care for now.
export interface Participant {
  avaId: string;
  avaName: string;
  userName: string;
  scribe: boolean;
  isTyping: boolean;
}

export interface Message {
  text: string;
  voiceGender?: TtsGender;
  ttsProvider?: 'google' | 'elevenlabs';
  voiceId?: string;
}

export interface UserTyping {
  avaId: string;
  userName: string;
}

//TODO Remove these 2 consts when we remove TTS 1.0
export const TTS_PLACEHOLDER_VOICE_ID = 'openai-echo';
export const TTS_PLACEHOLDER_GENDER = 'male';

export const sortVoiceByGender = (ttsVoices: TtSVoiceEntry, gender: TtsGender) => {
  const femaleVoices = new Map();
  const neutralVoices = new Map();
  const maleVoices = new Map();

  ttsVoices.forEach((voice, key) => {
    if (voice.gender === 'neutral') {
      neutralVoices.set(key, voice);
    } else if (voice.gender === 'female') {
      femaleVoices.set(key, voice);
    } else {
      maleVoices.set(key, voice);
    }
  });

  let sortedVoices: TtSVoiceEntry = new Map();

  if (gender === 'neutral') {
    sortedVoices = new Map([...neutralVoices, ...femaleVoices, ...maleVoices]);
  } else if (gender === 'female') {
    sortedVoices = new Map([...femaleVoices, ...neutralVoices, ...maleVoices]);
  } else {
    sortedVoices = new Map([...maleVoices, ...neutralVoices, ...femaleVoices]);
  }

  return sortedVoices;
};

export const isAudioPlaying = (audioElement: HTMLAudioElement) => {
  return !audioElement.paused && !audioElement.ended && audioElement.currentTime > 0;
};

export const stopAudio = (audioElement: HTMLAudioElement) => {
  audioElement.pause();
  audioElement.currentTime = 0;
};

export const getImageNameForVoice = (voice?: TtsVoice) => {
  if (!voice) return;
  const voiceImageMapping = [
    ['nova', 'alloy', 'echo'],
    ['shimmer', 'fable', 'onyx'],
  ];
  const index = voiceImageMapping.findIndex((row) => row.includes(voice.voiceProviderId));

  return `${voice.gender}${index + 1}`;
};

// Enhanced duration calculation using karaoke timing data
export const calculateTtsDuration = (text: string, voice?: TtsVoice, language?: string): number => {
  if (!text) return 2000; // Minimum duration

  // Fallback to simple calculation if we don't have voice/language data
  if (!voice?.voiceProviderId || !language) {
    return Math.max(2000, (text.length / 5) * (60 / 150) * 1000);
  }

  // Extract voice name from voiceProviderId (e.g., "openai-nova" -> "nova")
  const voiceName = voice.voiceProviderId.split('-').pop() as keyof typeof KARAOKE_PRE_DELAY_MAPPING;

  // Get language code (e.g., "en-US" -> "en")
  const langCode = language.split('-')[0] as 'en' | 'fr';

  // Check if we have timing data for this voice and language
  const hasTimingData =
    voiceName &&
    KARAOKE_PRE_DELAY_MAPPING[voiceName] !== undefined &&
    KARAOKE_DELAY_MAPPING[voiceName] !== undefined &&
    PUNCTUATION_DELAYS[voiceName] &&
    PUNCTUATION_DELAYS[voiceName][langCode];

  if (!hasTimingData) {
    // Fallback to simple calculation
    return Math.max(2000, (text.length / 5) * (60 / 150) * 1000);
  }

  // Get timing parameters for this voice
  // Pre-delay is currently set to 0 and can dynamically change if we notice
  // that the TTS sound is still active after the microphone gets unmuted.
  // it's a security measure to use KARAOKE_DELAY_MAPPING[voiceName], but
  // we are still figuring what are the best settings.
  const preDelay = 0;
  // const preDelay = KARAOKE_PRE_DELAY_MAPPING[voiceName];
  const baseDelay = KARAOKE_DELAY_MAPPING[voiceName];

  // Split text into words and calculate duration
  const words = text.trim().split(/\s+/);
  let totalDuration = preDelay; // Start with pre-delay

  words.forEach((word) => {
    // Base duration per word
    totalDuration += baseDelay;

    // Add punctuation delays
    const { isPunctuation, punctuationDelay } = checkEndsWithPunctuation(word, voiceName, langCode);
    if (isPunctuation) {
      totalDuration += punctuationDelay;
    }
  });

  // Ensure minimum duration
  return Math.max(2000, totalDuration);
};

export class TextToSpeechManager {
  ws: WebSocket;
  dispatch: AppDispatch;
  getState: () => RootState;
  activeTtsTimeout: NodeJS.Timeout | null = null;
  ttsStartTime: number | null = null;
  currentTtsEndTime: number | null = null;
  pendingTtsRequests: Array<{ text: string; estimatedDurationMs: number }> = [];

  constructor(ws: WebSocket, dispatch: AppDispatch, getState: () => RootState) {
    this.ws = ws;
    this.dispatch = dispatch;
    this.getState = getState;
    this.activeTtsTimeout = null;
    this.ttsStartTime = null;
    this.currentTtsEndTime = null;
    this.pendingTtsRequests = [];
    this.ws.addEventListener('message', (event) => {
      const message = JSON.parse(event.data);
      this.handleMessage(message);
    });
    this.handleMessage = this.handleMessage.bind(this);
  }

  handleMessage(response: Partial<RoomStatusUpdate | ResponseTextToSpeech>): void {
    if (response.type === 'room-status-update') {
      if (response.reason && response.reason === 'someone-is-typing') {
        const user = response.participants?.find((participant) => participant.isTyping === true);
        this.dispatch(setUserTyping(user));
      } else {
        if (response.participants) {
          const noLongerTyping = !response.participants.some((participant) => participant.isTyping === true);
          if (noLongerTyping) {
            this.dispatch(setUserTyping(undefined));
          }
        }
      }
    } else if (response.type === 'response-text-to-speech') {
      if (response.error) {
        this.dispatch(setTtsError(response.error as TtsErrors));
      }

      // Start timeout when backend finishes sending audio (actual playback begins)
      if (response.isDone) {
        this.handleTtsPlaybackStart();
      }
    }
  }

  handleTtsPlaybackStart() {
    // Process pending TTS requests when backend confirms audio is ready
    if (this.pendingTtsRequests.length === 0) {
      return;
    }

    const now = Date.now();

    // Calculate duration for the new requests only
    let newRequestsDurationMs = 0;
    for (const request of this.pendingTtsRequests) {
      newRequestsDurationMs += request.estimatedDurationMs;
    }

    // If TTS is already active, extend the end time
    if (this.activeTtsTimeout && this.currentTtsEndTime) {
      const remainingTime = Math.max(0, this.currentTtsEndTime - now);
      this.currentTtsEndTime = this.currentTtsEndTime + newRequestsDurationMs;
      const newTotalTimeoutMs = remainingTime + newRequestsDurationMs;

      // Cancel previous timeout since we're setting a new cumulative one
      clearTimeout(this.activeTtsTimeout);
      this.activeTtsTimeout = null;

      // Set new timeout for the extended duration
      this.activeTtsTimeout = setTimeout(() => {
        setTtsPlaybackActive(false);
        this.dispatch(unmuteMicForTts());
        this.activeTtsTimeout = null;
        this.ttsStartTime = null;
        this.currentTtsEndTime = null;
      }, newTotalTimeoutMs);
    } else {
      // First TTS playback, set start time and end time
      this.ttsStartTime = now;
      this.currentTtsEndTime = now + newRequestsDurationMs;

      // Set up duration-based unmuting
      this.activeTtsTimeout = setTimeout(() => {
        setTtsPlaybackActive(false);
        this.dispatch(unmuteMicForTts());
        this.activeTtsTimeout = null;
        this.ttsStartTime = null;
        this.currentTtsEndTime = null;
      }, newRequestsDurationMs);
    }

    // Clear pending requests as they are now being processed
    this.pendingTtsRequests = [];
  }

  handleTextToSpeech(message: Message) {
    // Get current voice and language from Redux state
    const state = this.getState();
    const currentVoice = selectCurrentSelectedVoice(state);
    const currentLanguage = selectSpeechLang(state);

    // Calculate enhanced duration using karaoke timing data
    const estimatedDurationMs = calculateTtsDuration(message.text, currentVoice, currentLanguage);

    // Store the request to be processed when backend confirms audio is ready
    this.pendingTtsRequests.push({
      text: message.text,
      estimatedDurationMs: estimatedDurationMs,
    });

    // Set TTS playback as active to help identify incoming tracks
    setTtsPlaybackActive(true);

    // Immediately mute microphone to prevent echo (or keep it muted if already muted for previous TTS)
    this.dispatch(muteMicForTts());

    // Send TTS request to backend
    sendWsMessage(this.ws, { type: 'request-text-to-speech', ...message });
  }
  handleSendIAmTyping(userTyping: UserTyping) {
    sendWsMessage(this.ws, { type: 'signal-i-am-typing', fromUser: userTyping });
  }
}

export const attemptSpeakWithVoice = ({
  message,
  recording,
  startRecord,
  stopRecord,
  voice,
}: {
  message: string;
  recording: boolean;
  startRecord: () => void;
  stopRecord: () => void;
  // $FlowFixMe
  voice: SpeechSynthesisVoice;
}) => {
  if (!window.speechSynthesis || !voice) return;

  const utterThis = new SpeechSynthesisUtterance(message);
  // 1 is the default, ranges from 0.1 to 10. 1 sounds a bit too fast.
  utterThis.rate = 0.7;
  utterThis.voice = voice;

  if (recording) {
    stopRecord();
    utterThis.onend = startRecord;
    utterThis.onerror = startRecord;
  }

  window.speechSynthesis.speak(utterThis);
};
